import React, { useCallback, useState } from 'react';

import { useAuth, supabase } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';

import { Button } from 'components/ui/Button';
import { TextInput } from 'components/ui/TextInput';

import { colors, Fonts, spacing } from 'styles/theme';
import { Bumper, HorizontalFlexWrapper } from 'styles/shared';

export const UpdateEmail = () => {
    const { userEmail, getUserData } = useAuth();
    const { showSuccessToast, showErrorToast } = useToast();

    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [newEmail, setNewEmail] = useState<string>('');
    const [status, setStatus] = useState<'idle' | 'pending' | 'sent' | 'error'>(
        'idle'
    );

    const onSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            setStatus('pending');
            const { error } = await supabase.auth.updateUser(
                { email: newEmail },
                { emailRedirectTo: `${location.origin}/email-confirmed` }
            );
            if (error) {
                setStatus('error');
                showErrorToast(error.message);
            } else {
                setStatus('sent');
                showSuccessToast(
                    'A confirmation email has been sent to your new address!'
                );
            }
        },
        [newEmail, showErrorToast, showSuccessToast]
    );

    const onToggleExpanded = useCallback(() => {
        if (isExpanded) {
            setIsExpanded(false);
            setNewEmail('');
            setStatus('idle');
        } else {
            setIsExpanded(true);
        }
    }, [isExpanded]);

    const isEmailValid = newEmail.trim() !== '' && newEmail.includes('@');
    const isSubmitting = status === 'pending';

    const onRefreshUserData = useCallback(async () => {
        await getUserData?.();
        setStatus('idle');
        onToggleExpanded();
    }, [getUserData, onToggleExpanded]);

    return (
        <>
            {isExpanded ? (
                <>
                    <div
                        style={{
                            margin: `${spacing.xLarge} 0`,
                            borderTop: `1px solid ${colors.grey10}`,
                            borderBottom: `1px solid ${colors.grey10}`,
                            padding: `${spacing.medium} 0`,
                        }}
                    >
                        {status === 'sent' ? (
                            <>
                                <Fonts.Small $color={colors.grandGreen}>
                                    A confirmation email has been sent to your
                                    new address. Please follow the instructions
                                    in the email to complete the update. You can
                                    use the button below to refresh your email
                                    data after confirming.
                                </Fonts.Small>
                                <Bumper />
                                <Button
                                    onClick={onRefreshUserData}
                                >{`Refresh Email Data`}</Button>
                            </>
                        ) : (
                            <>
                                <Fonts.Small $color={colors.grandGreen}>
                                    Current email adress: {userEmail}
                                </Fonts.Small>
                                <Bumper $minHeight={spacing.medium} />

                                <form onSubmit={onSubmit}>
                                    <TextInput
                                        type="email"
                                        value={newEmail}
                                        label="New email address:"
                                        onChange={(e) =>
                                            setNewEmail(e.target.value)
                                        }
                                        placeholder="Enter new email address"
                                    />
                                    <Bumper $minHeight={spacing.medium} />

                                    <HorizontalFlexWrapper>
                                        <Button
                                            onClick={onToggleExpanded}
                                            $style={
                                                isExpanded ? 'outline' : 'fill'
                                            }
                                        >
                                            Cancel
                                        </Button>
                                        <Bumper $minWidth={spacing.small} />
                                        <Button
                                            type="submit"
                                            $isDisabled={
                                                !isEmailValid || isSubmitting
                                            }
                                            $isLoading={isSubmitting}
                                        >
                                            Confirm New Email
                                        </Button>
                                    </HorizontalFlexWrapper>
                                </form>
                            </>
                        )}
                    </div>
                </>
            ) : (
                <HorizontalFlexWrapper>
                    <Button
                        onClick={onToggleExpanded}
                        $style={isExpanded ? 'outline' : 'fill'}
                    >
                        {'Update Email Address'}
                    </Button>
                </HorizontalFlexWrapper>
            )}
        </>
    );
};
